import { Injectable, Logger, ServiceUnavailableException } from '@nestjs/common';
import { connect } from 'node:net';

export interface AgentResult {
  ok: boolean;
  code: number;
  stdout: string;
  stderr: string;
  durationMs: number;
  timedOut?: boolean;
  parsed?: unknown;
}

/**
 * The only file in the API that talks to `db-maintenance-agent`.
 *
 * Everything privileged — pgbackrest, pg_ctl, VACUUM, restore — happens in that
 * daemon, not here. The API process runs as `deploy` and deliberately holds no
 * database superuser and no shell, because it is the internet-facing process.
 * If the API is ever compromised, the attacker's reach is the agent's verb
 * list and nothing more.
 *
 * Keeping this a single narrow file matters: it is the one place where a
 * careless addition would widen that boundary, so it is the one place to look
 * when reviewing whether the boundary still holds.
 */
@Injectable()
export class DbAgentClient {
  private readonly logger = new Logger(DbAgentClient.name);

  private socketPath(): string {
    return process.env.DBAGENT_SOCKET ?? '/run/dbagent/dbagent.sock';
  }

  private secret(): string {
    return process.env.DBAGENT_SECRET ?? '';
  }

  /**
   * True when the agent is configured at all.
   *
   * Every scheduled service checks this and returns quietly when it is false,
   * so a developer laptop — which has no agent, no pgBackRest and no cluster —
   * does not log an error every two minutes. Absence is a valid state, not a
   * fault.
   */
  isConfigured(): boolean {
    return Boolean(this.secret());
  }

  /**
   * Ask the agent to do one thing.
   *
   * `token` is required for the destructive verbs (restore, teardown,
   * maintenance) and is minted by `issueToken` only after the approval flow has
   * completed. The API cannot forge one, which is what stops a compromised API
   * from restoring over production even though it can reach the verb.
   */
  async call(verb: string, args: Record<string, unknown> = {}, token?: string): Promise<AgentResult> {
    if (!this.isConfigured()) {
      throw new ServiceUnavailableException(
        'db-maintenance-agent is not configured (DBAGENT_SECRET unset)'
      );
    }

    const payload = JSON.stringify({ verb, args, secret: this.secret(), token }) + '\n';

    return new Promise<AgentResult>((resolve, reject) => {
      const socket = connect(this.socketPath());
      let buffer = '';

      // Generous: a full backup or a restore legitimately takes many minutes.
      // The agent enforces its own hard ceiling per child process, so this is a
      // backstop against a wedged socket rather than the real timeout.
      socket.setTimeout(35 * 60 * 1000);

      socket.on('connect', () => socket.write(payload));
      socket.on('data', (chunk) => {
        buffer += chunk.toString('utf8');
      });

      socket.on('end', () => {
        try {
          const response = JSON.parse(buffer) as { ok: boolean; result?: AgentResult; error?: string };
          if (!response.ok) {
            reject(new ServiceUnavailableException(`agent rejected ${verb}: ${response.error}`));
            return;
          }
          resolve(response.result as AgentResult);
        } catch {
          reject(new ServiceUnavailableException(`agent returned unparseable output for ${verb}`));
        }
      });

      socket.on('timeout', () => {
        socket.destroy();
        reject(new ServiceUnavailableException(`agent timed out on ${verb}`));
      });

      socket.on('error', (err) => {
        this.logger.warn(`agent socket error on ${verb}: ${err.message}`);
        reject(new ServiceUnavailableException(`cannot reach db-maintenance-agent: ${err.message}`));
      });
    });
  }

  /**
   * Mint a single-use token for one destructive verb.
   *
   * Called only from the approved paths. The token is single-use and expires in
   * 15 minutes, so it cannot be stockpiled — an attacker who scrapes one from
   * memory gets one operation, once, within that window.
   */
  async issueToken(verb: string): Promise<string> {
    const result = (await this.call('token.issue', { verb })) as unknown as { token: string };
    return result.token;
  }

  /** `pgbackrest info --output json`, already parsed. */
  async backupInfo(stanza: string, repo?: number): Promise<PgBackRestInfo[]> {
    const result = await this.call('backup.info', { stanza, ...(repo ? { repo } : {}) });
    return (result.parsed as PgBackRestInfo[]) ?? [];
  }
}

/**
 * The shape of `pgbackrest info --output json`, narrowed to the fields this
 * codebase reads.
 *
 * Kept minimal on purpose: pgBackRest's JSON is large and version-dependent, so
 * every field named here is one more thing that can break on upgrade. The
 * database is the manifest of record — this output is a cross-check.
 */
export interface PgBackRestInfo {
  name: string;
  status: { code: number; message: string };
  archive: Array<{ id: string; min: string | null; max: string | null }>;
  backup: Array<{
    label: string;
    type: 'full' | 'diff' | 'incr';
    timestamp: { start: number; stop: number };
    lsn?: { start: string; stop: string };
    archive?: { start: string | null; stop: string | null };
    info: { size: number; repository: { size: number; delta?: number } };
    database: { id: number; 'repo-key'?: number };
  }>;
}
